"use client";

import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronRight, Target, Palette, Zap } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { fadeInUp, slideInFromLeft, slideInFromRight } from "@/utils/animations";
import { useLanguage } from "@/contexts/LanguageContext";

const iconMap = {
  "Target": Target,
  "Palette": Palette,
  "Zap": Zap,
} as const;

const Process = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { scrollXProgress } = useScroll({
    container: containerRef,
  });
  const isMobile = useIsMobile();
  const [activeStep, setActiveStep] = useState(0);
  const { t, getProcessSteps } = useLanguage();
  
  const processSteps = getProcessSteps();

  const x = useTransform(scrollXProgress, [0, 1], [0, -100]);

  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      if (!contentRef.current || !containerRef.current) return;

      const scrollLeft = containerRef.current.scrollLeft;
      const stepWidth = contentRef.current.scrollWidth / 3;
      const newActiveStep = Math.floor(scrollLeft / stepWidth);
      
      if (newActiveStep !== activeStep) {
        setActiveStep(newActiveStep);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [isMobile, activeStep]);

  const handleStepClick = (index: number) => {
    if (!contentRef.current || !containerRef.current) return;

    const stepWidth = contentRef.current.scrollWidth / 3;
    containerRef.current.scrollTo({
      left: stepWidth * index,
      behavior: "smooth",
    });
    setActiveStep(index);
  };

  return (
    <section id="process" className="py-20 md:py-32 bg-background theme-transition" aria-labelledby="process-title">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          className="mb-12 md:mb-20"
        >
          <h2 id="process-title" className="font-inter font-bold text-3xl sm:text-4xl md:text-5xl text-foreground mb-6">
            {t('process.title')}
          </h2>
          <p className="font-inter font-normal text-base md:text-lg text-foreground/80 max-w-3xl">
            {t('process.description')}
          </p>
        </motion.div>

        {/* Process Steps */}
        <div className="relative">
          {/* Step Indicators (Desktop) */}
          {!isMobile && (
            <div className="flex justify-between mb-12" role="tablist" aria-label="Photography process steps">
              {processSteps.map((step, index) => {
                const IconComponent = iconMap[step.icon as keyof typeof iconMap] || Target;
                return (
                  <button
                    key={step.number}
                    onClick={() => handleStepClick(index)}
                    className={`flex flex-col items-center transition-all duration-300 ${
                      activeStep === index ? "opacity-100" : "opacity-40 hover:opacity-80"
                    }`}
                    role="tab"
                    aria-selected={activeStep === index}
                    aria-controls={`process-step-${index}`}
                    id={`process-tab-${index}`}
                    aria-label={`View step ${step.number}: ${step.title}`}
                  >
                    <div className="flex items-center mb-4">
                      <span className="font-inter font-bold text-4xl text-foreground mr-4">
                        {step.number}
                      </span>
                      <div className={`w-16 h-px transition-all duration-300 ${
                        activeStep >= index ? "bg-primary" : "bg-border"
                      }`} aria-hidden="true" />
                    </div>
                    <span className="font-inter font-medium text-sm text-foreground">
                      {step.title}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Process Content */}
          <div
            ref={containerRef}
            className={`overflow-x-auto scrollbar-hide ${
              isMobile ? "snap-x snap-mandatory" : ""
            }`}
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
            aria-live="polite"
            aria-label="Photography process details"
          >
            <div
              ref={contentRef}
              className={`flex ${
                isMobile
                  ? "w-[300%] space-x-4"
                  : "w-full justify-between"
              }`}
            >
              {processSteps.map((step, index) => {
                const IconComponent = iconMap[step.icon as keyof typeof iconMap] || Target;
                
                return (
                  <motion.div
                    key={step.number}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={index % 2 === 0 ? slideInFromLeft : slideInFromRight}
                    className={`flex-shrink-0 ${
                      isMobile
                        ? "w-1/3 px-4 snap-center"
                        : "w-[30%]"
                    }`}
                    id={`process-step-${index}`}
                    role="tabpanel"
                    aria-labelledby={`process-tab-${index}`}
                    tabIndex={0}
                  >
                    <div className="h-full">
                      {/* Step Header (Mobile) */}
                      {isMobile && (
                        <div className="flex items-center mb-6">
                          <div className="flex items-center justify-center w-10 h-10 rounded-none border border-primary mr-4 theme-transition">
                            <IconComponent className="w-5 h-5 text-primary" aria-hidden="true" />
                          </div>
                          <div>
                            <div className="font-inter font-bold text-sm text-primary mb-1">
                              {step.number}
                            </div>
                            <h3 className="font-inter font-bold text-xl text-foreground">
                              {step.title}
                            </h3>
                          </div>
                        </div>
                      )}

                      {/* Step Content */}
                      <div className="relative">
                        {/* Connection Line (Desktop) */}
                        {!isMobile && index < processSteps.length - 1 && (
                          <div className="absolute top-8 -right-16 w-32 h-px bg-gradient-to-r from-primary to-border" aria-hidden="true" />
                        )}

                        {/* Step Icon (Desktop) */}
                        {!isMobile && (
                          <div className="flex items-center mb-6">
                            <div className="flex items-center justify-center w-12 h-12 rounded-none border border-primary mr-4 theme-transition">
                              <IconComponent className="w-6 h-6 text-primary" aria-hidden="true" />
                            </div>
                            <div>
                              <div className="font-inter font-bold text-sm text-primary mb-1">
                                {step.number}
                              </div>
                              <h3 className="font-inter font-bold text-2xl text-foreground">
                                {step.title}
                              </h3>
                            </div>
                          </div>
                        )}

                        {/* Step Description */}
                        <p className="font-inter font-normal text-base md:text-lg text-foreground/80 leading-relaxed mb-8">
                          {step.description}
                        </p>

                        {/* Technical Details */}
                        <ul className="space-y-3" aria-label="Technical details">
                          {step.technicalDetails.map((detail, detailIndex) => (
                            <li key={detailIndex} className="flex items-center">
                              <div className="w-2 h-2 bg-primary mr-3" aria-hidden="true" />
                              <span className="font-inter font-medium text-sm text-foreground/90">
                                {detail}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Mobile Navigation Dots with proper touch target size */}
          {isMobile && (
            <div className="flex justify-center mt-8 space-x-2" role="tablist" aria-label="Process step navigation">
              {processSteps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleStepClick(index)}
                  className={`relative p-3 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-primary ${
                    activeStep === index
                      ? "bg-primary/10"
                      : "hover:bg-border"
                  }`}
                  role="tab"
                  aria-selected={activeStep === index}
                  aria-label={`Go to step ${index + 1}`}
                >
                  <span className={`block w-3 h-3 rounded-full transition-all ${
                    activeStep === index
                      ? "bg-primary"
                      : "bg-border"
                  }`} />
                </button>
              ))}
            </div>
          )}

          {/* Desktop Navigation */}
          {!isMobile && (
            <div className="flex justify-center mt-12">
              <nav className="flex items-center space-x-4" aria-label="Process step numbers">
                {processSteps.map((step, index) => (
                  <button
                    key={step.number}
                    onClick={() => handleStepClick(index)}
                    className={`flex items-center font-inter font-medium text-sm transition-colors px-3 py-2 rounded ${
                      activeStep === index
                        ? "text-primary bg-primary/10"
                        : "text-foreground/80 hover:text-primary hover:bg-primary/5"
                    }`}
                    aria-label={`View ${step.title} step`}
                  >
                    <span className="mr-2">{step.number}</span>
                    {index < processSteps.length - 1 && (
                      <ChevronRight className="w-4 h-4 mx-2" aria-hidden="true" />
                    )}
                  </button>
                ))}
              </nav>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Process;